import axios from 'axios';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { api } from 'services/api';
import { PAGE_SIZE } from 'services/constants/env';
import { notificationContainer } from 'utils/notificationContainer';
import { getSupportedPostalOperatorLastListParams } from './selectors';
import {
	clearSupportedPostalOperatorDetails,
	createSupportedPostalOperatorRequest,
	deleteSupportedPostalOperatorFailure,
	deleteSupportedPostalOperatorRequest,
	deleteSupportedPostalOperatorSuccess,
	getSupportedPostalOperatorDetailsFailure,
	getSupportedPostalOperatorDetailsRequest,
	getSupportedPostalOperatorDetailsSuccess,
	getSupportedPostalOperatorListFailure,
	getSupportedPostalOperatorListRequest,
	getSupportedPostalOperatorListSuccess,
	saveSupportedPostalOperatorFailure,
	saveSupportedPostalOperatorSuccess,
	updateSupportedPostalOperatorRequest,
} from './reducer';
import {
	TCreateOrUpdateSupportedPostalOperatorRequest,
	TSupportedPostalOperator,
	TSupportedPostalOperatorIdRequest,
	TSupportedPostalOperatorListResponse,
	TSupportedPostalOperatorsManagementRequest,
	TUpdateSupportedPostalOperatorRequest,
} from './types';

const DEFAULT_LIST_PARAMS: TSupportedPostalOperatorsManagementRequest = {
	pageSize: PAGE_SIZE,
	pageNumber: 1,
};

/** Shows the message the API returned, falling back to the passed default. */
function notifyError(error: unknown, fallback: string) {
	if (axios.isAxiosError(error)) {
		notificationContainer(
			String(error?.response?.data?.message ?? error.message ?? fallback),
			'error',
		);
		return;
	}
	notificationContainer(fallback, 'error');
}

function* getSupportedPostalOperatorListWorker({
	payload,
}: PayloadAction<TSupportedPostalOperatorsManagementRequest>) {
	try {
		const response: TSupportedPostalOperatorListResponse = yield call(
			api.supportedPostalOperators.getList,
			payload,
		);
		yield put(getSupportedPostalOperatorListSuccess(response));
	} catch (error) {
		yield put(getSupportedPostalOperatorListFailure());
		notifyError(error, 'Failed to load supported postal operators');
	}
}

function* refetchSupportedPostalOperatorList() {
	const lastParams: TSupportedPostalOperatorsManagementRequest | null = yield select(
		getSupportedPostalOperatorLastListParams,
	);
	const response: TSupportedPostalOperatorListResponse = yield call(
		api.supportedPostalOperators.getList,
		lastParams ?? DEFAULT_LIST_PARAMS,
	);
	yield put(getSupportedPostalOperatorListSuccess(response));
}

function* getSupportedPostalOperatorDetailsWorker({
	payload,
}: PayloadAction<TSupportedPostalOperatorIdRequest>) {
	try {
		const response: TSupportedPostalOperator = yield call(
			api.supportedPostalOperators.getDetails,
			payload,
		);
		yield put(getSupportedPostalOperatorDetailsSuccess(response));
	} catch (error) {
		yield put(getSupportedPostalOperatorDetailsFailure());
		notifyError(error, 'Failed to load supported postal operator');
	}
}

function* createSupportedPostalOperatorWorker({
	payload,
}: PayloadAction<TCreateOrUpdateSupportedPostalOperatorRequest>) {
	try {
		yield call(api.supportedPostalOperators.create, payload);
		yield call(refetchSupportedPostalOperatorList);
		yield put(saveSupportedPostalOperatorSuccess());
		notificationContainer('Supported postal operator was successfully created', 'success');
	} catch (error) {
		yield put(saveSupportedPostalOperatorFailure());
		notifyError(error, 'Failed to create supported postal operator');
	}
}

function* updateSupportedPostalOperatorWorker({
	payload,
}: PayloadAction<TUpdateSupportedPostalOperatorRequest>) {
	try {
		const response: TSupportedPostalOperator = yield call(
			api.supportedPostalOperators.update,
			payload,
		);
		yield put(getSupportedPostalOperatorDetailsSuccess(response));
		yield call(refetchSupportedPostalOperatorList);
		yield put(saveSupportedPostalOperatorSuccess());
		notificationContainer('Supported postal operator was successfully updated', 'success');
	} catch (error) {
		yield put(saveSupportedPostalOperatorFailure());
		notifyError(error, 'Failed to update supported postal operator');
	}
}

function* deleteSupportedPostalOperatorWorker({
	payload,
}: PayloadAction<TSupportedPostalOperatorIdRequest>) {
	try {
		yield call(api.supportedPostalOperators.remove, payload);
		yield put(clearSupportedPostalOperatorDetails());
		yield call(refetchSupportedPostalOperatorList);
		yield put(deleteSupportedPostalOperatorSuccess());
		notificationContainer('Supported postal operator was successfully deleted', 'success');
	} catch (error) {
		yield put(deleteSupportedPostalOperatorFailure());
		notifyError(error, 'Failed to delete supported postal operator');
	}
}

export function* supportedPostalOperatorsSaga() {
	yield takeLatest(
		getSupportedPostalOperatorListRequest.type,
		getSupportedPostalOperatorListWorker,
	);
	yield takeLatest(
		getSupportedPostalOperatorDetailsRequest.type,
		getSupportedPostalOperatorDetailsWorker,
	);
	yield takeLatest(createSupportedPostalOperatorRequest.type, createSupportedPostalOperatorWorker);
	yield takeLatest(updateSupportedPostalOperatorRequest.type, updateSupportedPostalOperatorWorker);
	yield takeLatest(deleteSupportedPostalOperatorRequest.type, deleteSupportedPostalOperatorWorker);
}
